// Registro de eliminados: la memoria de "qué eliminamos".
// Consume GET /almacenamiento/registro (paginado en el servidor).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, DatePicker, Empty, Input, Pagination, Skeleton, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { ClearOutlined } from "@ant-design/icons";
import { UserCell } from "../../../Components/UserAvatar";
import {
  useFiltroPersistido,
  useRangoPersistido,
} from "../../../hooks/useFiltroPersistido";
import { formatPropertyNames } from "../../../utils/propertyDisplay";
import { paginacionServidor } from "../../../utils/tablaPaginacion";
import {
  almacenamientoApi,
  type RegistroEliminado,
} from "../../../services/almacenamiento.api";
import { diaISO, formatDiaChile, formatFechaHoraChile, formatPeso } from "../almacenamientoUtils";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const UsosRegistro: React.FC<{ r: RegistroEliminado }> = ({ r }) => {
  if (r.usos.length === 0) {
    return <Tag color="orange">Ya era huérfano</Tag>;
  }
  return (
    <div className="alm-dueno">
      {r.usos.map((u, i) => {
        const props = formatPropertyNames(u.propiedades);
        return (
          <div key={`${u.modulo}-${i}`} className="alm-registro-uso">
            <Tag color="purple" style={{ marginInlineEnd: 6 }}>
              {u.etiquetaModulo}
            </Tag>
            <Text>{u.titulo}</Text>
            {props.length > 0 && (
              <Text type="secondary" className="alm-dueno-sub">
                {" "}
                · {props.join(" · ")}
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AvisoHuerfano: React.FC<{ r: RegistroEliminado }> = ({ r }) =>
  r.quedoHuerfano ? (
    <Tooltip title="El archivo quedó sin dueño en el sistema pero sigue ocupando espacio en el disco.">
      <Tag color="warning">Quedó huérfano en disco</Tag>
    </Tooltip>
  ) : null;

const RegistroEliminados: React.FC<{ esMovil: boolean }> = ({ esMovil }) => {
  const [busqueda, setBusqueda] = useFiltroPersistido<string | undefined>(
    "almacenamiento.registroBusqueda",
    undefined,
    { validar: (v) => v === undefined || typeof v === "string" },
  );
  const [texto, setTexto] = useState<string>(() => busqueda ?? "");
  useEffect(() => {
    setTexto(busqueda ?? "");
  }, [busqueda]);
  const [rango, setRango] = useRangoPersistido("almacenamiento.registroRango", null);

  const [filas, setFilas] = useState<RegistroEliminado[]>([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticion = useRef(0);

  const desde = diaISO(rango?.[0]);
  const hasta = diaISO(rango?.[1]);

  const cargar = useCallback(
    async (p: number, s: number) => {
      const id = ++peticion.current;
      setCargando(true);
      try {
        const res = await almacenamientoApi.registro({
          page: p,
          size: s,
          q: busqueda || undefined,
          desde,
          hasta,
        });
        if (id !== peticion.current) return;
        if (!res) {
          setError(true);
          return;
        }
        setError(false);
        setFilas(res.data || []);
        setTotal(res.total || 0);
        setPage(res.page || p);
        setSize(res.size || s);
      } finally {
        if (id === peticion.current) setCargando(false);
      }
    },
    [busqueda, desde, hasta],
  );

  useEffect(() => {
    void cargar(1, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, desde, hasta]);

  const paginar = (p: number, s: number) => void cargar(s !== size ? 1 : p, s);
  const config = paginacionServidor({
    page,
    size,
    total,
    etiqueta: ["registro", "registros"],
    onChange: paginar,
  });

  const columnas: ColumnsType<RegistroEliminado> = [
    {
      title: "Cuándo (hora Chile)",
      dataIndex: "creadoEn",
      width: 160,
      render: (v: string) => formatFechaHoraChile(v),
    },
    {
      title: "Quién eliminó",
      key: "quien",
      width: 200,
      render: (_, r) =>
        r.usuarioNombre ? (
          <UserCell nombre={r.usuarioNombre} size={26} />
        ) : (
          <Text type="secondary">Usuario no disponible</Text>
        ),
    },
    {
      title: "Archivo",
      key: "archivo",
      width: 260,
      render: (_, r) => (
        <div className="alm-dueno">
          <Text ellipsis={{ tooltip: r.nombre }} className="alm-nombre">
            {r.nombre}
          </Text>
          <Text type="secondary" className="alm-dueno-sub">
            Subido: {r.fechaSubida ? formatDiaChile(r.fechaSubida) : "fecha desconocida"}
          </Text>
        </div>
      ),
    },
    {
      title: "Peso",
      dataIndex: "pesoBytes",
      width: 100,
      align: "right",
      render: (v: number) => <Text strong>{formatPeso(v)}</Text>,
    },
    {
      title: "Dónde se usaba",
      key: "usos",
      width: 320,
      render: (_, r) => <UsosRegistro r={r} />,
    },
    {
      title: "Observación",
      key: "obs",
      width: 190,
      render: (_, r) => <AvisoHuerfano r={r} />,
    },
  ];

  return (
    <div>
      <section className="alm-filtros" aria-label="Filtros del registro">
        <div className="alm-filtros-grid alm-filtros-registro">
          <Input.Search
            allowClear
            placeholder="Buscar por archivo, quién eliminó o dueño"
            aria-label="Buscar en el registro"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              if (!e.target.value) setBusqueda(undefined);
            }}
            onSearch={(v) => setBusqueda(v.trim() || undefined)}
          />
          <RangePicker
            format="DD-MM-YYYY"
            value={rango as [Dayjs, Dayjs] | null}
            placeholder={["Eliminado desde", "Eliminado hasta"]}
            onChange={(vals) => setRango(vals && vals[0] && vals[1] ? [vals[0], vals[1]] : null)}
            allowClear
          />
          <Button
            className="alm-btn-tactil"
            icon={<ClearOutlined />}
            disabled={!busqueda && !rango}
            onClick={() => {
              setTexto("");
              setBusqueda(undefined);
              setRango(null);
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      </section>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el registro de eliminados"
          action={
            <Button onClick={() => void cargar(1, size)} loading={cargando}>
              Reintentar
            </Button>
          }
        />
      ) : esMovil ? (
        cargando && filas.length === 0 ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : filas.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Todavía no se ha eliminado ningún archivo desde esta página"
          />
        ) : (
          <div className="alm-tarjetas" aria-busy={cargando}>
            {filas.map((r) => (
              <article key={r.id} className="alm-card" aria-label={r.nombre}>
                <Text strong ellipsis className="alm-card-nombre">
                  {r.nombre}
                </Text>
                <div className="alm-card-datos">
                  <Text strong>{formatPeso(r.pesoBytes)}</Text>
                  <Text type="secondary">{formatFechaHoraChile(r.creadoEn)}</Text>
                </div>
                <div>
                  <Text type="secondary">Eliminó: </Text>
                  <Text>{r.usuarioNombre ?? "usuario no disponible"}</Text>
                </div>
                <UsosRegistro r={r} />
                <AvisoHuerfano r={r} />
              </article>
            ))}
            <Pagination
              className="alm-paginador-movil"
              size="small"
              showLessItems
              current={config.current}
              pageSize={config.pageSize}
              total={config.total}
              showSizeChanger={config.showSizeChanger}
              pageSizeOptions={config.pageSizeOptions}
              showTotal={config.showTotal}
              onChange={config.onChange}
            />
          </div>
        )
      ) : (
        <Table<RegistroEliminado>
          className="alm-tabla"
          rowKey="id"
          size="middle"
          loading={cargando}
          dataSource={filas}
          columns={columnas}
          pagination={config}
          scroll={{ x: 1250 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Todavía no se ha eliminado ningún archivo desde esta página"
              />
            ),
          }}
        />
      )}
    </div>
  );
};

export default RegistroEliminados;
